import React, { createContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import apiClient from '../services/api';

export const AuthorizationContext = createContext();

/**
 * Authorization Context Provider
 * Manages user permissions and accessible resources from the authorization system
 *
 * Features:
 * - Fetches accessible resources on login
 * - Caches resource permissions
 * - Provides permission checking methods
 */
export const AuthorizationProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [accessibleResources, setAccessibleResources] = useState([]);
  const [resourcePermissions, setResourcePermissions] = useState({});
  const [loading, setLoading] = useState(true);

  // Fetch accessible resources when user logs in
  useEffect(() => {
    if (user && isAuthenticated) {
      fetchAccessibleResources();
    } else {
      setAccessibleResources([]);
      setResourcePermissions({});
      setLoading(false);
    }
  }, [user, isAuthenticated]);

  const fetchAccessibleResources = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/Authorization/accessible-resources');

      if (response.data.success) {
        setAccessibleResources(response.data.resources);
      }
    } catch (error) {
      console.error('Failed to fetch accessible resources:', error);
      setAccessibleResources([]);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Check if user has access to a resource
   * @param {string} resourceCode - Resource code (e.g., "SchoolStudentMaster")
   * @returns {boolean} - True if user has access
   */
  const hasAccess = (resourceCode) => {
    return accessibleResources.includes(resourceCode);
  };

  /**
   * Get permissions for a specific resource
   * Caches the result for performance
   * @param {string} resourceCode - Resource code
   * @returns {Promise<Object>} - Permissions object
   */
  const getResourcePermissions = async (resourceCode) => {
    // Return cached permissions if available
    if (resourcePermissions[resourceCode]) {
      return resourcePermissions[resourceCode];
    }

    try {
      const response = await apiClient.get(`/Authorization/permissions/${resourceCode}`);

      if (response.data.success) {
        const permissions = response.data.permissions;

        // Cache the permissions
        setResourcePermissions(prev => ({
          ...prev,
          [resourceCode]: permissions
        }));

        return permissions;
      }

      return null;
    } catch (error) {
      console.error(`Failed to fetch permissions for ${resourceCode}:`, error);
      return null;
    }
  };

  /**
   * Check if user can submit data for a resource
   * @param {string} resourceCode - Resource code
   * @returns {Promise<boolean>} - True if user can submit
   */
  const canSubmit = async (resourceCode) => {
    const permissions = await getResourcePermissions(resourceCode);
    return permissions?.canSubmit || false;
  };

  /**
   * Get data scope for a resource
   * @param {string} resourceCode - Resource code
   * @returns {Promise<string>} - Data scope (OWN_SCHOOL, OWN_DISTRICT, ALL, NONE)
   */
  const getDataScope = async (resourceCode) => {
    const permissions = await getResourcePermissions(resourceCode);
    return permissions?.dataScope || 'NONE';
  };

  /**
   * Check if user can reopen submission for a resource
   * @param {string} resourceCode - Resource code
   * @returns {Promise<boolean>} - True if user can reopen submission
   */
  const canReopenSubmission = async (resourceCode) => {
    const permissions = await getResourcePermissions(resourceCode);
    return permissions?.canReopenSubmission || false;
  };

  /**
   * Check if user can manage school for a resource
   * @param {string} resourceCode - Resource code
   * @returns {Promise<boolean>} - True if user can manage school
   */
  const canManageSchool = async (resourceCode) => {
    const permissions = await getResourcePermissions(resourceCode);
    return permissions?.canManageSchool || false;
  };

  /**
   * Clear cached permissions (useful after permission updates)
   */
  const clearPermissionsCache = () => {
    setResourcePermissions({});
  };

  const value = {
    accessibleResources,
    resourcePermissions,
    loading,
    hasAccess,
    getResourcePermissions,
    canSubmit,
    getDataScope,
    canReopenSubmission,
    canManageSchool,
    clearPermissionsCache,
    refetch: fetchAccessibleResources
  };

  return (
    <AuthorizationContext.Provider value={value}>
      {children}
    </AuthorizationContext.Provider>
  );
};
